type JsonRecord = Record<string, unknown>;

export class AuthApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'AuthApiError';
  }
}

export interface AuthSession {
  access_token?: string;
  refresh_token?: string;
  cookie_authenticated?: boolean;
  token_type?: string;
  expires_in?: number;
  expires_at?: number;
}

export interface AuthApiResponse {
  ok?: boolean;
  session: AuthSession;
  user?: JsonRecord | null;
  error?: string;
  code?: string;
  retry_after_seconds?: number;
}

export type PasswordResetResponse = {
  success?: boolean;
  message?: string;
  email?: string;
  status?: string;
  was_pending_activation?: boolean;
  verified_at?: string;
};

export type PublicAuthDependencies = {
  getApiBaseUrl: () => string;
  getApiKey?: () => string | undefined;
  fetch?: typeof globalThis.fetch;
};

const asRecord = (value: unknown): JsonRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : null;

const readString = (source: JsonRecord, keys: string[]): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const readNumber = (source: JsonRecord, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
};

export const normalizeAuthPayload = (payload: unknown): AuthApiResponse => {
  const top = asRecord(payload) || {};
  const nestedData = asRecord(top.data) || {};
  const source = 'session' in top || 'user' in top ? top : nestedData;
  const nestedSession = asRecord(source.session) || {};
  return {
    ok: typeof top.ok === 'boolean' ? top.ok : undefined,
    session: {
      access_token: readString(nestedSession, ['access_token', 'accessToken'])
        || readString(source, ['access_token', 'accessToken']),
      refresh_token: readString(nestedSession, ['refresh_token', 'refreshToken'])
        || readString(source, ['refresh_token', 'refreshToken']),
      cookie_authenticated: nestedSession.cookie_authenticated === true,
      token_type: readString(nestedSession, ['token_type', 'tokenType']) || undefined,
      expires_in: readNumber(nestedSession, ['expires_in', 'expiresIn']),
      expires_at: readNumber(nestedSession, ['expires_at', 'expiresAt']),
    },
    user: asRecord(source.user) || asRecord(nestedData.user),
    error: readString(top, ['error']) || undefined,
    code: readString(top, ['code']) || undefined,
    retry_after_seconds: readNumber(top, ['retry_after_seconds']),
  };
};

export const createPublicAuthApi = (dependencies: PublicAuthDependencies) => {
  const fetchRequest = dependencies.fetch ?? globalThis.fetch;

  const request = async (path: string, body: JsonRecord): Promise<unknown> => {
    const apiBaseUrl = dependencies.getApiBaseUrl().trim().replace(/\/$/, '');
    if (!apiBaseUrl) throw new Error('VITE_AI_API_URL is not configured');
    const apiKey = dependencies.getApiKey?.()?.trim();
    const csrfToken = typeof document === 'undefined'
      ? ''
      : document.cookie
        .split(';')
        .map((value) => value.trim())
        .find((value) => value.startsWith('dohockey_csrf='))
        ?.slice('dohockey_csrf='.length) || '';
    const response = await fetchRequest(`${apiBaseUrl}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'x-api-key': apiKey } : {}),
        ...(csrfToken ? { 'X-CSRF-Token': decodeURIComponent(csrfToken) } : {}),
      },
      body: JSON.stringify(body),
    });
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    if (!response.ok) {
      const record = asRecord(payload);
      const message = record
        ? readString(record, ['message', 'detail', 'error'])
        : '';
      throw new AuthApiError(message || `Auth API request failed (${response.status})`, response.status);
    }
    return payload;
  };

  return {
    loginWithPassword: async (email: string, password: string): Promise<AuthApiResponse> => {
      return normalizeAuthPayload(await request('/api/v1/auth/login/password', { email, password }));
    },
    requestMagicLink: async (email: string, redirectTo?: string): Promise<void> => {
      await request('/api/v1/auth/login/otp/request', {
        email,
        ...(redirectTo ? { redirect_to: redirectTo } : {}),
      });
    },
    verifyMagicLink: async (email: string, token: string): Promise<AuthApiResponse> => {
      return normalizeAuthPayload(await request('/api/v1/auth/login/otp/verify', {
        email,
        token,
        type: /^\d{6}$/.test(token.trim()) ? 'email' : 'magiclink',
      }));
    },
    requestPasswordReset: async (email: string, language: string): Promise<void> => {
      await request('/api/v1/auth/password/reset/request', { email, language });
    },
    verifyPasswordReset: async (
      email: string,
      code: string,
      newPassword: string,
    ): Promise<PasswordResetResponse> => {
      const payload = await request('/api/v1/auth/password/reset/verify', {
        email,
        code,
        new_password: newPassword,
      });
      return asRecord(payload) as PasswordResetResponse || {};
    },
  };
};
